import { useState } from "react";
import { Cylinder, Lock, LogIn, Mail } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Login({ onForgot }) {
  const { login, logoutReason, clearLogoutReason } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await login(email.trim(), password);
    setBusy(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] bg-grid flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-8">
          <div className="h-12 w-12 rounded-lg bg-amber-500/10 border border-amber-500/40 flex items-center justify-center">
            <Cylinder className="h-7 w-7 text-amber-400" />
          </div>
          <h1 className="text-2xl font-extrabold tracking-tight font-mono text-slate-100">TANK VOLUME CALCULATOR</h1>
        </div>

        <form onSubmit={submit} className="bg-[#131924] border border-[#243046] rounded-xl p-8 shadow-2xl space-y-5 rise" data-testid="login-form">
          {logoutReason && (
            <p data-testid="logout-reason" className="text-sm text-amber-300 font-mono bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
              {logoutReason}
            </p>
          )}
          <div>
            <h2 className="text-lg font-semibold font-mono text-slate-100">Sign in</h2>
            <p className="text-xs text-slate-500 font-mono mt-1">Authorized personnel only. Contact your administrator for access.</p>
          </div>

          <div>
            <p className="text-[11px] font-mono text-slate-500 mb-1">Email</p>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                data-testid="login-email-input"
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  clearLogoutReason();
                }}
                placeholder="you@company.com"
                className="pl-9 bg-[#0D131E] border-[#243046] font-mono text-slate-100 focus-visible:ring-amber-500"
              />
            </div>
          </div>

          <div>
            <p className="text-[11px] font-mono text-slate-500 mb-1">Password</p>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-9 bg-[#0D131E] border-[#243046] font-mono text-slate-100 focus-visible:ring-amber-500"
              />
            </div>
          </div>

          {error && (
            <p data-testid="login-error" className="text-sm text-red-400 font-mono bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full bg-amber-500 text-[#0B0F17] hover:bg-amber-400 font-semibold">
            <LogIn />
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <button type="button" data-testid="forgot-password-link" onClick={onForgot} className="w-full text-xs font-mono text-slate-500 hover:text-amber-300 transition-colors">
            Forgot password?
          </button>
        </form>
      </div>
    </div>
  );
}
